import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, pct, biasColor, marketLabel } from "@/lib/format";
import { MarketingLayout } from "@/components/MarketingLayout";
import { Seo } from "@/components/Seo";
import { DemoBadge } from "@/components/DemoBadge";
import { CardSkeleton } from "@/components/Bits";

const tabs = ["all", "crypto", "forex", "stocks", "indices", "commodities"];

export default function Markets() {
  const [assets, setAssets] = useState(null);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => setAssets([]));
  }, []);

  const rows = (assets || []).filter((a) => tab === "all" || a.market === tab);

  return (
    <MarketingLayout>
      <Seo title="Markets" description="Crypto, forex, stocks, indices and commodities analysed by KREONIS. Demo data." />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Markets</span>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">Global market overview</h1>
            <p className="mt-2 max-w-xl text-muted-foreground">Prices and analytics are generated by the KREONIS demo engine.</p>
          </div>
          <DemoBadge />
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button key={t} data-testid={`market-tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-secondary"}`}>
              {t === "all" ? "All" : marketLabel[t]}
            </button>
          ))}
        </div>

        {!assets ? (
          <div className="mt-8"><CardSkeleton count={6} /></div>
        ) : (
          <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-sm" data-testid="markets-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Asset</th>
                  <th className="px-4 py-3">Market</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-right">Change</th>
                  <th className="px-4 py-3 text-right">RSI</th>
                  <th className="px-4 py-3 text-right">Trend</th>
                  <th className="px-4 py-3 text-right">AI Bias</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.symbol} data-testid={`market-row-${a.symbol.replace(/\W/g, "-").toLowerCase()}`} className="border-b border-border last:border-0 hover:bg-secondary/50">
                    <td className="px-4 py-3"><div className="font-display font-semibold">{a.symbol}</div><div className="text-xs text-muted-foreground">{a.name}</div></td>
                    <td className="px-4 py-3 capitalize text-muted-foreground">{a.market}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(a.price)}</td>
                    <td className={`px-4 py-3 text-right font-mono ${a.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(a.change_pct)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt(a.rsi, 1)}</td>
                    <td className={`px-4 py-3 text-right capitalize ${biasColor(a.trend)}`}>{a.trend}</td>
                    <td className={`px-4 py-3 text-right font-semibold capitalize ${biasColor(a.ai_bias)}`}>{a.ai_bias}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MarketingLayout>
  );
}
